import { OVERRIDE_TO_API_KEY_MAP, OVERRIDE_METHOD_NAME_MAP } from './types';
import type {
  OverridableMethodsMap,
  OverrideMethodsType,
  OverrideStoreType
} from './types';

class OverridesRegistryStore {
  private _store: OverrideStoreType = {};

  /**
   * Registers overrides from a strongly-typed OverrideMethodsType object.
   * API names and method names are mapped to their APIv1Type equivalents.
   *
   * @example
   * store.registerFromType({
   *   session: { isLoggedIn: () => true },
   *   authProvider: { getAccessToken: async () => 'token' }
   * });
   */
  registerFromType(overrides: OverrideMethodsType): void {
    for (const [apiName, methods] of Object.entries(overrides)) {
      if (!methods) continue;

      const typedApiName = apiName as keyof OverridableMethodsMap;
      const apiKey = OVERRIDE_TO_API_KEY_MAP[typedApiName];
      const nameMap = OVERRIDE_METHOD_NAME_MAP[typedApiName];

      for (const [methodName, fn] of Object.entries(methods)) {
        if (!fn) continue;

        const methodMapName =
          nameMap?.[methodName as keyof typeof nameMap] ?? methodName;

        this._register(
          apiKey,
          methodMapName,
          fn as (...args: unknown[]) => unknown
        );
      }
    }
  }

  private _register(
    apiName: string,
    methodName: string,
    implementation: (...args: unknown[]) => unknown
  ): void {
    const key = apiName.toLowerCase();
    if (!this._store[key]) {
      this._store[key] = {};
    }
    this._store[key][methodName] = implementation;
  }

  unregister<
    D extends keyof OverridableMethodsMap,
    M extends keyof OverridableMethodsMap[D]
  >(apiName: D, methodName: M): void {
    const storeKey = OVERRIDE_TO_API_KEY_MAP[apiName].toLowerCase();
    const storeMethodName = (
      OVERRIDE_METHOD_NAME_MAP[apiName] as Record<string, string>
    )[methodName as string];

    if (this._store[storeKey]) {
      delete this._store[storeKey][storeMethodName];
    }
  }

  get<
    D extends keyof OverridableMethodsMap,
    M extends keyof OverridableMethodsMap[D]
  >(apiName: D, methodName: M): OverridableMethodsMap[D][M] | undefined {
    const storeKey = OVERRIDE_TO_API_KEY_MAP[apiName].toLowerCase();
    const storeMethodName = (
      OVERRIDE_METHOD_NAME_MAP[apiName] as Record<string, string>
    )[methodName as string];

    return this._store[storeKey]?.[storeMethodName] as
      | OverridableMethodsMap[D][M]
      | undefined;
  }

  has<
    D extends keyof OverridableMethodsMap,
    M extends keyof OverridableMethodsMap[D]
  >(apiName: D, methodName: M): boolean {
    const storeKey = OVERRIDE_TO_API_KEY_MAP[apiName].toLowerCase();
    const storeMethodName = (
      OVERRIDE_METHOD_NAME_MAP[apiName] as Record<string, string>
    )[methodName as string];

    return !!this._store[storeKey]?.[storeMethodName];
  }

  clear(): void {
    this._store = {};
  }
}

export const overridesRegistryStore = new OverridesRegistryStore();
